import { useState } from 'react';
import { format } from 'date-fns';
import { getProfilePageUrl } from 'constants/pages';
import { isUuidFormatValid } from 'utils/shared/string/isUuidFormatValid';
import Edit from 'svg/Edit';
import Star from 'svg/Star';
import type { PlayerProfileReview, PlayerProfileView } from 'screens/PlayerProfile/types';
import Button from 'components/Button';
import CardsSection from 'components/CardSection';
import SharedReviewCard from 'components/ReviewCard';

interface Props {
  reviews: PlayerProfileReview[];
  stats: PlayerProfileView['stats'];
  /** Gated upstream: a signed-in visitor who has played with them and hasn't reviewed yet. */
  canWriteReview?: boolean;
  onWriteReview?: () => void;
}

/** How many the design shows before "Show all reviews" — one desktop grid row. */
const COLLAPSED_COUNT = 2;

/** Adapter over the shared review card: player reviews carry no coaching modality
 *  or external source, but they do link back to the reviewer's profile. */
function ReviewCard({ review }: { review: PlayerProfileReview }) {
  const { reviewer } = review;
  // An imported review has no reviewer row, only the external name.
  const name = reviewer?.fullName || reviewer?.preferredName || review.externalFullName || 'Player';
  // A UUID is the signup placeholder, so there's no profile worth linking to.
  const username =
    reviewer?.username && !isUuidFormatValid(reviewer.username) ? reviewer.username : null;

  return (
    <SharedReviewCard
      name={name}
      avatarPath={reviewer?.profileImagePath}
      dateLabel={format(new Date(review.createdAt), 'MMMM d, yyyy')}
      rating={review.rating}
      review={review.review}
      profileHref={username ? getProfilePageUrl(username) : null}
    />
  );
}

export default function ProfileReviews({
  reviews,
  stats,
  canWriteReview = false,
  onWriteReview,
}: Props) {
  const [isShowingAll, setIsShowingAll] = useState(false);
  const hasReviews = stats.reviewCount > 0;
  const writtenCount = reviews.filter((review) => !!review.review).length;
  const canShowMore = reviews.length > COLLAPSED_COUNT;

  return (
    <section className="flex flex-col gap-ds-md md:gap-ds-lg">
      {hasReviews ? (
        <>
          <div className="flex flex-col gap-ds-sm">
            <h2 className="typography-product-subheading-mobile md:typography-product-heading-desktop flex items-center gap-ds-sm text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              <span>Reviews {stats.reviewCount}</span>
              {stats.averageRating != null && (
                <span className="flex items-center gap-ds-xs">
                  <Star className="h-4 w-4 md:h-6 md:w-6" />
                  {stats.averageRating.toFixed(1)}
                </span>
              )}
            </h2>
            <p className="typography-product-text-card-mobile md:typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {stats.reviewCount} total {stats.reviewCount === 1 ? 'rating' : 'ratings'},{' '}
              {writtenCount} with reviews
            </p>
          </div>

          {isShowingAll ? (
            // Expanded, the carousel becomes the two-column grid the design shows,
            // so every loaded review is reachable without a horizontal scroll.
            <div className="grid grid-cols-1 gap-ds-xl md:grid-cols-2 md:gap-ds-2xl">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          ) : (
            <CardsSection
              data={reviews}
              gap="2rem"
              visibleCards={{ base: 1.15, xs: 1.15, sm: 2, md: 2, lg: 2 }}
              renderCard={(review) => <ReviewCard review={review} />}
            />
          )}

          {canShowMore && (
            <Button
              variant="secondary"
              size="md"
              isInline
              className="self-center md:self-end"
              onClick={() => setIsShowingAll((value) => !value)}
            >
              {isShowingAll ? 'Show fewer reviews' : 'Show all reviews'}
            </Button>
          )}
        </>
      ) : (
        <h2 className="typography-product-subheading-mobile md:typography-product-heading-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          No reviews yet
        </h2>
      )}

      {canWriteReview && (
        <Button
          variant="primary"
          size="md"
          isInline
          className="self-start"
          onClick={onWriteReview}
          iconLeft={<Edit className="h-4 w-4" />}
        >
          Write a review
        </Button>
      )}
    </section>
  );
}
